import { ProtectedComponent } from '@components'

import { ModuleLayout, Space } from '@billy/design-system'
import { ReactElement, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetchAccounts } from '@features/accounts/hooks/useFetchAccounts'
import { BankingOnboardingEmptyState } from '@features/bankAccounts/elements/BankingOnboardingEmptyState'
import { useAutoOpenConnectBankModal } from '@features/bankAccounts/hooks/useAutoOpenConnectBankModal'
import { useBankAccountListData } from '@features/bankAccounts/hooks/useBankAccountListData'
import { useShouldShowBankingOnboarding } from '@features/bankAccounts/hooks/useShouldShowBankingOnboarding'
import { consumeBankDemoAutoOpenRequest } from '@features/bankAccounts/utils/bankDemoAutoOpenStore'
import { useBankTransactionsSandbox } from '@features/bankTransactions/hooks/useBankTransactionsSandbox'
import { QuickStartStep } from '@features/quickStart/enums/quickStartStep'
import { useQuickStartProgress } from '@features/quickStart/hooks'
import { UiSource } from '@features/reconciliation/enums'
import { BankTransactionsSandbox } from '@views/bankTransactions/routes/BankTransactionsSandbox'

import { Scope } from '../../../../enums/scope'
import { BankAccountList } from './elements/BankAccountList'
import { BankAccountSideActions } from './elements/BankAccountSideActions'
import { BankAccountsSkeleton } from './elements/BankAccountsSkeleton'
import { BankUpsellCard } from './elements/BankUpsellCard'
import { OpenBankingConnectionModule } from './elements/OpenBankingConnectionModule'

export const BankAccounts = (): ReactElement => {
  const { t } = useTranslation()
  const { isLoading, meta } = useFetchAccounts()
  const { hasConnectedBankAccount, shouldShowBankUpsell } = useBankAccountListData()
  const { isLoading: isLoadingConnectionState, shouldShowBankingOnboarding } = useShouldShowBankingOnboarding()
  const { markStepDone } = useQuickStartProgress()
  const { isSandboxOpen, closeSandbox, openSandbox } = useBankTransactionsSandbox(consumeBankDemoAutoOpenRequest)

  useAutoOpenConnectBankModal()

  // The bootstrap endpoint seeds the react-query cache with a partial accounts subset before /v2/accounts resolves.
  // Bootstrap data has no `meta` while the real /v2/accounts response does — so we use `meta` to distinguish
  // between the two and only render the list once the real data has loaded.
  const isFirstAccountsFetch = isLoading || !meta || isLoadingConnectionState

  // Handles already-connected bank accounts (Shine Banking or another bank)
  // to ensure the quick start step isn't skipped.
  useEffect(() => {
    if (hasConnectedBankAccount) {
      markStepDone(QuickStartStep.Bank)
    }
  }, [hasConnectedBankAccount, markStepDone])

  const shouldShowEmptyState = !isFirstAccountsFetch && shouldShowBankingOnboarding

  return (
    <ModuleLayout
      title={t('bank.accounts.title')}
      sideActions={isFirstAccountsFetch || shouldShowEmptyState ? undefined : <BankAccountSideActions />}
    >
      {isFirstAccountsFetch && <BankAccountsSkeleton />}
      {shouldShowEmptyState && (
        <BankingOnboardingEmptyState onTryDemo={openSandbox} uiSource={UiSource.AccountPageEmptyState} />
      )}
      {!isFirstAccountsFetch && !shouldShowEmptyState && (
        <>
          <BankAccountList onOpenSandbox={openSandbox} />
          <Space size="s16" />
          {shouldShowBankUpsell && (
            <ProtectedComponent scopes={Scope.BankSyncWrite}>
              <BankUpsellCard />
              <Space size="s16" />
            </ProtectedComponent>
          )}
          <ProtectedComponent scopes={Scope.BankSyncWrite}>
            <OpenBankingConnectionModule />
          </ProtectedComponent>
        </>
      )}
      {isSandboxOpen && <BankTransactionsSandbox onClose={closeSandbox} />}
    </ModuleLayout>
  )
}
